import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useLogger } from '../../../services/logging/logger-context';
import { useLazyFetchNearbyPoisQuery } from '../../../services/ocm.api';
import { FinderFilters, FinderOrigin, FinderResult } from './types';
import { getOcmConnectionTypeIds } from './utils/connectors';
import { findNearbyCandidates } from './utils/search';

export type NearbySearchState =
  | { phase: 'idle' }
  | { phase: 'loading'; radiusKm: number }
  | { phase: 'error'; messageKey: string }
  | { phase: 'done'; origin: FinderOrigin; candidates: FinderResult[] };

/** Marks a new call as the latest one and returns a check for whether it still is */
const beginCall = (latestCallRef: MutableRefObject<number>) => {
  latestCallRef.current += 1;
  const call = latestCallRef.current;
  return () => call === latestCallRef.current;
};

/**
 * Runs nearby charger searches. Only the latest search or error is shown: anything that
 * finishes after a newer call is ignored.
 */
export const useNearbyChargerSearch = () => {
  const { logger } = useLogger();
  const [fetchNearbyPois] = useLazyFetchNearbyPoisQuery();
  const [state, setState] = useState<NearbySearchState>({ phase: 'idle' });
  const latestCallRef = useRef(0);

  // Abandon any running search when the finder closes
  useEffect(
    () => () => {
      latestCallRef.current += 1;
    },
    [],
  );

  const showError = useCallback((messageKey: string) => {
    beginCall(latestCallRef);
    setState({ phase: 'error', messageKey });
  }, []);

  const search = useCallback(
    async (origin: FinderOrigin, filters: FinderFilters) => {
      const isLatest = beginCall(latestCallRef);
      const connectionTypeIds = getOcmConnectionTypeIds(filters.connectorTypes);
      const fetchPoisWithinKm = (radiusKm: number) =>
        fetchNearbyPois(
          {
            latitude: origin.latitude,
            longitude: origin.longitude,
            radiusKm,
            connectionTypeIds,
            levelId: filters.level,
          },
          true,
        ).unwrap();

      try {
        const candidates = await findNearbyCandidates(fetchPoisWithinKm, origin, filters, {
          onRadiusStart: (radiusKm) => setState({ phase: 'loading', radiusKm }),
          isCancelled: () => !isLatest(),
        });
        if (isLatest()) setState({ phase: 'done', origin, candidates });
      } catch (error) {
        if (!isLatest()) return;
        logger.error('Nearby charger search failed', error as Error);
        setState({ phase: 'error', messageKey: 'nearby_chargers_search_error' });
      }
    },
    [fetchNearbyPois, logger],
  );

  return { state, search, showError };
};
